import storage from "redux-persist/lib/storage";
import { StrictMode } from "react";
import { configureStore } from "@reduxjs/toolkit";
import { persistConstants } from "./constants/persist";
import { persistReducer, persistStore } from "redux-persist";
import groceryCrudReducers from "./reducers";
import serverRequestMiddleware from "./middlewares/server-request-middleware";
import urlMiddleware from "./middlewares/url-middleware";
import multipleSelectionsMiddleware from "./middlewares/multiple-selections-middleware";
import persistMiddleware from "./middlewares/persist-middleware";
import { configurationActions } from "./actions/configuration-actions";
import App from "./App";
import ReactDOM from "react-dom/client";
import externalEvents from "./middlewares/external-events";
import callbackEvents from "./middlewares/callback-events";
import { SKIN_DEFAULT, THEME_DEFAULT } from "./constants/themes";

const groceryCrudLoader = (myElement, settings = {}) => {
  const {
    apiUrl,
    landingPageUrl,
    uniqueId,
    theme: themeDataSet,
    loadCssTheme = "true",
    loadCssIcons = "true",
    loadCssThirdParty = "true",
    skin: skinDataSet,
    masterPrimaryKeyValue,
    rememberState = "true",
    rememberFiltering: rememberFilteringState = "true",
    showThemeSwitcher: showThemeSwitcherDataSet = "false",
  } = myElement.dataset;

  const publishEvents = myElement.dataset.publishEvents === "true";
  const hasCache = rememberState === "true";
  const rememberFiltering = rememberFilteringState === "true";
  const skin = skinDataSet || localStorage.getItem("gc-skin") || SKIN_DEFAULT;
  const theme =
    themeDataSet || localStorage.getItem("gc-theme") || THEME_DEFAULT;
  const showThemeSwitcher = showThemeSwitcherDataSet === "true";

  const {
    callbackBeforeInsert,
    callbackBeforeUpdate,
    callbackAfterInsert,
    callbackAfterUpdate,
    callbackBeforeDelete,
    callbackAfterDelete,
    onRowUpdate,
    onRowUnmount,
    addFields,
    editFields,
    cloneFields,
    readFields,
    actionButtons,
    actionButtonsMultiple,
  } = settings;

  let configurationSettings = {
    callbackBeforeInsert,
    callbackBeforeUpdate,
    callbackAfterInsert,
    callbackAfterUpdate,
    callbackBeforeDelete,
    callbackAfterDelete,
    onRowUpdate,
    onRowUnmount,
    addFields,
    editFields,
    cloneFields,
    readFields,
    actionButtons,
    actionButtonsMultiple,
  };

  const persistConfig = {
    key: uniqueId,
    storage,
    blacklist: rememberFiltering
      ? persistConstants.blacklistedKeys
      : [...persistConstants.blacklistedKeys, "search"],
  };

  let persistedReducer;

  if (hasCache) {
    persistedReducer = persistReducer(persistConfig, groceryCrudReducers);
  }

  const groceryCrudStore = configureStore({
    reducer: hasCache ? persistedReducer : groceryCrudReducers,
    middleware: [
      persistMiddleware,
      serverRequestMiddleware,
      urlMiddleware,
      multipleSelectionsMiddleware,
      externalEvents(publishEvents),
      callbackEvents(configurationSettings),
    ],
    devTools: true,
  });

  groceryCrudStore.dispatch({
    type: configurationActions.MAIN_CONFIGURATION,
    apiUrl,
    landingPageUrl,
    masterPrimaryKeyValue,
    skin,
    theme,
  });

  // If there is no cache we don't have to wait for any persist events to start
  // with the init fetch
  if (!hasCache) {
    groceryCrudStore.dispatch({
      type: configurationActions.INIT_FETCH,
    });
  }

  let groceryCrudPersistor;

  if (hasCache) {
    groceryCrudPersistor = persistStore(groceryCrudStore);
  }

  const root = ReactDOM.createRoot(myElement);

  root.render(
    <StrictMode>
      <App
        {...{
          groceryCrudPersistor,
          groceryCrudStore,
          loadCssIcons: loadCssIcons === "true",
          loadCssTheme: loadCssTheme === "true",
          loadCssThirdParty: loadCssThirdParty === "true",
          settings,
          skin,
          theme,
          configurationSettings,
          publishEvents,
          showThemeSwitcher,
        }}
      />
    </StrictMode>
  );
};

if (window) {
  // Also make it available globally
  window.groceryCrudLoader = groceryCrudLoader;
}

export default groceryCrudLoader;
